// SPDX-License-Identifier: AGPL-3.0-or-later

import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router';

/**
 * Wraps every router navigation in `document.startViewTransition` so the
 * browser cross-fades + slides the old → new screen using the CSS rules
 * in `apps/web/src/styles.css` (`::view-transition-old/new(page)`).
 *
 * Bundle cost: 0 bytes — uses the native View Transitions API. Browsers
 * that don't support it (Firefox today) fall through to the standard
 * synchronous render, which is also what `prefers-reduced-motion`
 * users get.
 *
 * Why not framer-motion? It costs ~12 KB gzipped and ships its own
 * animation runtime. The View Transitions API gives us the same
 * cross-fade + 8px translate with declarative CSS and no JS payload.
 *
 * This hook attaches a `viewTransitionName: 'page'` to the document
 * `<body>` for the duration of the transition so the
 * `::view-transition-*(page)` rules match. The name is removed after
 * the transition resolves so other surfaces (modals, signature pad)
 * stay un-named and don't participate.
 */
interface ViewTransition {
  finished: Promise<void>;
}
type StartViewTransition = (callback: () => void) => ViewTransition;

export function usePageTransition(): void {
  const location = useLocation();
  const navType = useNavigationType();
  // Track the previous pathname so we only fire on real navigations
  // (not the first mount, which would flicker the initial render).
  const previous = useRef<string | null>(null);

  useEffect(() => {
    const current = location.pathname;
    if (previous.current === null) {
      previous.current = current;
      return;
    }
    if (previous.current === current) return;
    previous.current = current;

    // POP navigations (back/forward) skip the transition — the browser
    // handles those well enough on its own and the animation can feel
    // wrong relative to the back direction.
    if (navType === 'POP') return;

    const startViewTransition: StartViewTransition | undefined = (
      document as Document & { startViewTransition?: StartViewTransition }
    ).startViewTransition?.bind(document);
    if (startViewTransition === undefined) return;

    // React already committed the new tree before this effect runs, so
    // we can't wrap the commit itself. Instead we flash the
    // `view-transition-name` on at this point: the browser snapshots
    // the current frame for the "old" state, then runs the callback
    // synchronously (which is a no-op here because the DOM is already
    // updated) and snapshots again for the "new" state.
    //
    // The visual effect is identical to wrapping a router transition
    // — the cross-fade + slide come from the CSS keyframes; the
    // callback exists only to satisfy the API.
    document.documentElement.style.setProperty('view-transition-name', 'page');
    const transition = startViewTransition(() => {
      // No-op: DOM already reflects the new route.
    });
    void transition.finished.finally(() => {
      document.documentElement.style.removeProperty('view-transition-name');
    });
  }, [location.pathname, navType]);
}
